import Menu, { MenuProps } from 'core/components/dialogs/Menu'
import { AutosuggestProps } from 'core/components/inputs/autosuggest/Autosuggest'
import Tag from 'core/components/misc/Tag'
import { memo, useMemo } from 'react'
import { ID } from 'types/entity'

interface MenuContentProps<T extends { token_id: ID }>
  extends AutosuggestProps<T> {
  onMenuItemOnChangeCB: (
    v:
      | string
      | number
      | T['token_id']
      | (string | number | T['token_id'])[]
      | null
  ) => void
  highlightedItem: number
  footer?: MenuProps<T>['footer']
  header?: MenuProps<T>['header']
}

const MenuContent = memo(
  <T extends { token_id: ID }>({
    onMenuItemOnChangeCB,
    getDisplayValue,
    highlightedItem,
    options,
    value,
    menuVariant,
    useTagOptions,
    footer,
    header
  }: MenuContentProps<T>) => {
    const optionEls = useMemo(() => {
      if (useTagOptions) {
        return options?.map((opt) => {
          if (typeof opt === 'string' || typeof opt === 'number')
            return (
              <Tag key={opt} value={opt}>
                {opt}
              </Tag>
            )
          if ('token_id' in opt)
            return (
              <Tag
                key={opt.token_id}
                value={opt.token_id}
                prefix={opt.prefix}
                suffix={opt.suffix}
              >
                {getDisplayValue?.(opt) ?? opt.token_id}
              </Tag>
            )
          throw new Error(
            'Autosuggest cannot accept ReactElement or 2D options when useTagOptions=true'
          )
        })
      }
      return options
    }, [getDisplayValue, options, useTagOptions])

    return (
      <Menu
        className="max-h-72"
        options={optionEls}
        value={value}
        onChange={onMenuItemOnChangeCB}
        highlightedIndex={highlightedItem}
        variant={menuVariant}
        footer={footer}
        header={header}
      />
    )
  }
)

export default MenuContent
